import type { ExtraPayment } from '../../utils/loanAmortization';
import { isValidMonthKey } from '../../utils/loanAmortization';
import { isBlankLoanPlannerSnapshot } from '../../utils/loanPlannerSeed';
import {
  fetchLoanPlannerRemote,
  pushLoanPlannerRemote,
} from '../../services/loanPlannerService';

/**
 * Snapshot of Loan tab edits (amount, rate, term, start month, extras).
 * Source of truth is GET/PUT /loans/planner — nothing is stored on device.
 */
export type LoanPlannerSnapshot = {
  loanId: string;
  principal: number;
  interestRatePct: number;
  termYears: number;
  extras: ExtraPayment[];
  recurringExtra: number;
  /** First payment month `YYYY-MM`. Null until the user picks one. */
  startMonth: string | null;
  updatedAt: string;
};

function isValidSnapshot(value: unknown): value is LoanPlannerSnapshot {
  if (!value || typeof value !== 'object') return false;
  const s = value as Partial<LoanPlannerSnapshot>;
  return (
    typeof s.loanId === 'string' &&
    typeof s.principal === 'number' &&
    typeof s.interestRatePct === 'number' &&
    typeof s.termYears === 'number' &&
    typeof s.updatedAt === 'string' &&
    Array.isArray(s.extras ?? []) &&
    (s.startMonth == null || isValidMonthKey(s.startMonth))
  );
}

function coerceSnapshot(snapshot: LoanPlannerSnapshot): LoanPlannerSnapshot {
  return {
    ...snapshot,
    extras: snapshot.extras ?? [],
    recurringExtra: snapshot.recurringExtra ?? 0,
    startMonth: isValidMonthKey(snapshot.startMonth) ? snapshot.startMonth : null,
  };
}

function snapshotTimestamp(snapshot: LoanPlannerSnapshot | null | undefined): number {
  if (!snapshot?.updatedAt) return 0;
  const ms = Date.parse(snapshot.updatedAt);
  return Number.isFinite(ms) ? ms : 0;
}

/** Prefer the snapshot with the newer updatedAt (last-write-wins). */
export function pickNewerLoanPlannerSnapshot(
  a: LoanPlannerSnapshot | null,
  b: LoanPlannerSnapshot | null,
): LoanPlannerSnapshot | null {
  if (!a) return b;
  if (!b) return a;
  return snapshotTimestamp(a) >= snapshotTimestamp(b) ? a : b;
}

/**
 * Prefer real loan data over the blank $300k placeholder, even if the blank
 * row has a newer updatedAt (that usually means a buggy auto-save).
 */
export function pickPreferredLoanPlannerSnapshot(
  a: LoanPlannerSnapshot | null,
  b: LoanPlannerSnapshot | null,
): LoanPlannerSnapshot | null {
  if (!a) return b;
  if (!b) return a;
  const aBlank = isBlankLoanPlannerSnapshot(a);
  const bBlank = isBlankLoanPlannerSnapshot(b);
  if (aBlank && !bBlank) return b;
  if (bBlank && !aBlank) return a;
  return pickNewerLoanPlannerSnapshot(a, b);
}

export async function loadLoanPlannerSnapshot(
  loanId: string,
): Promise<LoanPlannerSnapshot | null> {
  try {
    const remote = await fetchLoanPlannerRemote(loanId);
    const planner = remote.planner;
    if (planner && isValidSnapshot(planner) && !isBlankLoanPlannerSnapshot(planner)) {
      const coerced = coerceSnapshot(planner);
      // Shared partner loan: keep the requested id so we never PUT a copy
      // onto this user.
      return remote.shared ? { ...coerced, loanId } : coerced;
    }
  } catch {
    // Offline / signed out
  }
  return null;
}

export async function saveLoanPlannerSnapshot(
  snapshot: LoanPlannerSnapshot,
): Promise<void> {
  if (isBlankLoanPlannerSnapshot(snapshot)) return;
  await syncLoanPlannerRemote(snapshot);
}

export async function hydrateLoanPlannerSnapshot(
  loanId: string,
): Promise<LoanPlannerSnapshot | null> {
  return loadLoanPlannerSnapshot(loanId);
}

/**
 * Try several loan ids (canonical + legacy) against the backend and return
 * the best real snapshot (display-only — never copy onto this user).
 */
export async function hydrateBestLoanPlannerSnapshot(
  candidateIds: string[],
  canonicalLoanId: string,
): Promise<LoanPlannerSnapshot | null> {
  const uniqueIds = Array.from(new Set(candidateIds.filter(Boolean)));
  let winner: LoanPlannerSnapshot | null = null;

  for (const id of uniqueIds) {
    const candidate = await hydrateLoanPlannerSnapshot(id);
    winner = pickPreferredLoanPlannerSnapshot(winner, candidate);
  }

  if (!winner || isBlankLoanPlannerSnapshot(winner)) return null;

  // Display under the canonical id. Do not PUT — that would copy a partner's
  // shared household loan onto this user.
  return {
    ...coerceSnapshot(winner),
    loanId: canonicalLoanId,
  };
}

export async function syncLoanPlannerRemote(
  snapshot: LoanPlannerSnapshot,
): Promise<void> {
  try {
    await pushLoanPlannerRemote(snapshot);
  } catch {
    // Offline / unauthenticated
  }
}
